import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import {
  ouvirAgendamentosDoLead,
  registrarComparecimento,
  statusPorId,
} from '@/services/agendamentosService';
import DialogoReagendar from '@/components/leads/DialogoReagendar';
import DialogoBaixaComparecimento from '@/components/agenda/DialogoBaixaComparecimento';
import DialogoBaixaFalta from '@/components/agenda/DialogoBaixaFalta';
import { ETAPA_AO_CANCELAR } from '@/agenda/cancelamento';
import { renomearLead } from '@/services/leadsService';
import { ehAvanco } from '@/leads/progresso';
import { GRADE_SUGERIDA } from '@/agenda/grade';

const exibir = (valor) => {
  const data = typeof valor?.toDate === 'function' ? valor.toDate() : valor;
  return data ? format(data, 'dd/MM/yy HH:mm') : '—';
};

/**
 * Consultas do lead: marcar, e registrar comparecimento ou falta.
 *
 * ★ VQ-2 — "Agendar" abre o MESMO diálogo do funil (`DialogoEtapa` com
 * `{ lead, etapa: 'agendado' }`, via `aoAgendar`): dia + grade de horários,
 * conflitos e "remarcar". O formulário próprio deste bloco (data solta, sem
 * grade) saiu — era o segundo caminho para o mesmo `criarAgendamento`.
 *
 * `clinicaId`, `autor` e `clinica` por PROPS (V2), pelo mesmo motivo do
 * `BlocoTarefas`: o bloco pode viver fora do `LeadsProvider`.
 *
 * A lista inclui as consultas CANCELADAS (VQ-3): quem está em "Não compareceu"
 * por cancelamento precisa ver isso aqui, não uma falta.
 */
export default function BlocoAgendamento({ lead, clinicaId, autor, clinica, equipe = [], aoAgendar }) {
  const [agendamentos, setAgendamentos] = useState([]);
  const [erro, setErro] = useState(null);
  // VQ-correção 1: falha do listener aparece aqui — nunca vira "Nenhuma consulta marcada".
  const [erroCarga, setErroCarga] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const [reagendando, setReagendando] = useState(null);
  /**
   * ★★ WI — a baixa "Compareceu" ganhou DIÁLOGO também aqui, e NÃO é uma segunda tela: é o
   * MESMO `DialogoBaixaComparecimento` da agenda, com o MESMO `CamposValores` do funil.
   *
   * ⚠ ELE TINHA DE ENTRAR AQUI TAMBÉM. A WI removeu das rules o passe que deixava a baixa
   * entrar em `compareceu` incompleta (`formaDaBaixaDaAgenda`); sem o diálogo, ESTE botão
   * passaria a dar `permission-denied` — um botão quebrado novo, que é exatamente o defeito
   * que a peça veio consertar. Ver `docs/wi-plano.md` §0, custo 2.
   */
  const [baixando, setBaixando] = useState(null);
  // ★★ WI: o "Faltou" da ficha pede o próximo contato pelo MESMO diálogo da agenda.
  const [faltando, setFaltando] = useState(null);

  useEffect(() => {
    setAgendamentos([]);
    setErroCarga(null);
    return ouvirAgendamentosDoLead(clinicaId, lead.id, (lista) => { setErroCarga(null); setAgendamentos(lista); }, (e) => {
      console.error('[OdontoSDR] Falha ao ouvir agendamentos:', e);
      setErroCarga('Não foi possível carregar as consultas deste lead. Tente de novo; se persistir, avise o suporte.');
    });
  }, [clinicaId, lead.id]);

  async function executar(acao) {
    setErro(null);
    setOcupado(true);
    try {
      await acao();
    } catch (e) {
      setErro(e.message ?? 'Não foi possível concluir a ação.');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <section className="bloco-tarefas">
      <div className="bloco-titulo">
        <h3>Consultas</h3>
        <button type="button" className="btn-secundario" disabled={ocupado} onClick={() => aoAgendar?.(lead)}>
          Agendar
        </button>
      </div>

      {erroCarga && <p className="erro" role="alert">{erroCarga}</p>}
      {agendamentos.length === 0 && !erroCarga && (
        <p className="vazio compacto">Nenhuma consulta marcada.</p>
      )}

      <ul className="lista-tarefas">
        {agendamentos.map((a) => {
          const status = statusPorId(a.status);
          return (
            <li key={a.id} className={a.status === 'cancelado' ? 'consulta-cancelada' : undefined}>
              <div>
                <strong>{exibir(a.dataHora)}</strong>
                <span className="tarefa-quando">
                  {a.procedimento || 'Procedimento não informado'}
                  {a.status === 'cancelado' && a.canceladoMotivo ? ` · cancelada: ${a.canceladoMotivo}` : ''}
                </span>
              </div>
              {a.status === 'agendado' ? (
                <div className="item-acoes">
                  <button
                    type="button"
                    className="btn-secundario"
                    disabled={ocupado}
                    onClick={() => {
                      // ★ `ehAvanco` falso → a etapa não se move, não há o que exigir: grava
                      // direto, como antes. Caso contrário, o diálogo pede código, nome e valor.
                      if (ehAvanco(lead?.etapa, 'compareceu')) { setBaixando(a); return; }
                      executar(() =>
                        // Lead e config já estão em memória: poupa duas leituras ao serviço.
                        registrarComparecimento(clinicaId, a, 'compareceu', autor, {
                          lead,
                          config: clinica?.config ?? null,
                        }),
                      );
                    }}
                  >
                    Compareceu
                  </button>
                  <button
                    type="button"
                    className="btn-secundario"
                    disabled={ocupado}
                    onClick={() => {
                      // ★ WE (b): o lead vai junto aqui também. A trava do retrocesso precisa da etapa
                      // atual nos DOIS botões, e aqui ela é de graça — o lead já está no escopo.
                      // ★★ WI: e se a etapa VAI se mover, o próximo contato é obrigatório.
                      if (ehAvanco(lead?.etapa, ETAPA_AO_CANCELAR)) { setFaltando(a); return; }
                      executar(() =>
                        registrarComparecimento(clinicaId, a, 'faltou', autor, { lead }),
                      );
                    }}
                  >
                    Faltou
                  </button>
                  <button
                    type="button"
                    className="btn-secundario"
                    disabled={ocupado}
                    onClick={() => setReagendando(a)}
                  >
                    Reagendar
                  </button>
                </div>
              ) : (
                <span className="pilula pequena" style={{ background: status.cor }}>
                  {status.label}
                </span>
              )}
            </li>
          );
        })}
      </ul>

      {erro && <p className="erro">{erro}</p>}

      {/* A lista deste bloco vem de `ouvirAgendamentosDoLead` (listener), então
          a data nova chega sozinha — não precisa de atualização local aqui. */}
      <DialogoBaixaFalta
        aberto={Boolean(faltando)}
        lead={lead}
        consulta={faltando}
        clinica={clinica}
        ocupado={ocupado}
        aoFechar={() => setFaltando(null)}
        aoConfirmar={async (retornoEm) => {
          await executar(async () => {
            await registrarComparecimento(clinicaId, faltando, 'faltou', autor, { lead, retornoEm });
            setFaltando(null);
          });
        }}
      />

      <DialogoBaixaComparecimento
        aberto={Boolean(baixando)}
        lead={lead}
        consulta={baixando}
        clinica={clinica}
        ocupado={ocupado}
        equipe={equipe}
        usuario={{ uid: autor?.id }}
        aoFechar={() => setBaixando(null)}
        aoConfirmar={async (dados) => {
          await executar(async () => {
            await registrarComparecimento(clinicaId, baixando, 'compareceu', autor, {
              lead,
              config: clinica?.config ?? null,
              dados,
            });
            // ★ A renomeação em lote próprio, depois — ela também toca as conversas vinculadas.
            const nomeNovo = String(dados.nome ?? '').trim();
            if (nomeNovo && nomeNovo !== String(lead?.nome ?? '').trim()) {
              await renomearLead(clinicaId, lead, nomeNovo, autor);
            }
            setBaixando(null);
          });
        }}
      />

      <DialogoReagendar
        aberto={Boolean(reagendando)}
        agendamento={reagendando}
        clinicaId={clinicaId}
        lead={lead}
        grade={clinica?.config?.agendaAvaliacao ?? GRADE_SUGERIDA}
        autor={autor}
        aoFechar={() => setReagendando(null)}
      />
    </section>
  );
}
